import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteComment, useUpdateComment } from 'actions/comments';
import Stack from 'components/Stack';
import Time from 'components/Time';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import InlineList from 'components/InlineList';
import CommentBubble from './CommentBubble';
import CommentBody from './CommentBody';
import DeleteConfirmation from './DeleteConfirmation';
import CommentForm from '../CommentForm';
import CommentFormTitleBar from '../CommentForm/CommentFormTitleBar';
import { useCommentsContext } from '../Context';
import { CommentAvatarWrapper } from '../Shared';
function Comment(_ref) {
  var isNested = _ref.isNested,
    isUserComment = _ref.isUserComment,
    comment = _ref.comment,
    size = _ref.size,
    onReply = _ref.onReply;
  var id = comment.id,
    author = comment.author,
    message = comment.message,
    isDeleted = comment.isDeleted,
    createdTime = comment.createdTime,
    updatedTime = comment.updatedTime,
    canDelete = comment.canDelete,
    canEdit = comment.canEdit;
  var isEdited = createdTime !== updatedTime;
  var _useTranslation = useTranslation(),
    _useTranslation2 = _slicedToArray(_useTranslation, 1),
    t = _useTranslation2[0];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isConfirmingDeletion = _useState2[0],
    setIsConfirmingDeletion = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isEditing = _useState4[0],
    setIsEditing = _useState4[1];
  var _useState5 = useState('idle'),
    _useState6 = _slicedToArray(_useState5, 2),
    submissionStatus = _useState6[0],
    setSubmissionStatus = _useState6[1];
  var _useCommentsContext = useCommentsContext(),
    contentId = _useCommentsContext.contentId,
    contentType = _useCommentsContext.contentType;
  var _useUpdateComment = useUpdateComment({
      contentId: contentId,
      contentType: contentType,
      onSuccess: function onSuccess() {
        return setSubmissionStatus('success');
      },
      onError: function onError() {
        return setSubmissionStatus('error');
      }
    }),
    updateComment = _useUpdateComment.updateComment;
  var _useDeleteComment = useDeleteComment({
      contentId: contentId,
      contentType: contentType
    }),
    deleteComment = _useDeleteComment.deleteComment;
  function submitEdit(_x) {
    return _submitEdit.apply(this, arguments);
  }
  function _submitEdit() {
    _submitEdit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(newMessage) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setIsEditing(false);
            if (!(newMessage !== message)) {
              _context.next = 4;
              break;
            }
            _context.next = 4;
            return updateComment({
              id: id,
              message: newMessage
            });
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _submitEdit.apply(this, arguments);
  }
  var isDefaultSize = size === 'DEFAULT';
  function onDeleteComment() {
    return _onDeleteComment.apply(this, arguments);
  }
  function _onDeleteComment() {
    _onDeleteComment = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            setIsConfirmingDeletion(false);
            _context2.next = 3;
            return deleteComment(id);
          case 3:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return _onDeleteComment.apply(this, arguments);
  }
  if (isEditing) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CommentFormTitleBar, {
      isNested: isNested,
      title: t('actions_edit_comment'),
      onClose: function onClose() {
        return setIsEditing(false);
      }
    }), /*#__PURE__*/React.createElement(CommentAvatarWrapper, {
      isNested: isNested,
      pictureUrl: author.pictureUrl
    }, /*#__PURE__*/React.createElement(CommentForm, {
      focusOnMount: true,
      initialValue: message,
      inputLabel: t('actions_edit_comment'),
      submitButtonLabel: t('actions_submit'),
      submitSuccessMessage: t('alert_comment_updated_success'),
      onSubmit: submitEdit,
      size: size,
      submissionStatus: submissionStatus,
      onResetSubmitStatus: function onResetSubmitStatus() {
        return setSubmissionStatus('idle');
      }
    })));
  } else {
    return /*#__PURE__*/React.createElement(CommentAvatarWrapper, {
      isNested: isNested,
      pictureUrl: author.pictureUrl
    }, /*#__PURE__*/React.createElement(Stack, {
      spacing: "xxs"
    }, /*#__PURE__*/React.createElement(CommentBubble, {
      isHighlighted: isUserComment
    }, /*#__PURE__*/React.createElement(CommentBody, {
      isDeleted: !!isDeleted,
      authorName: author.displayName,
      message: message
    })), !(isDeleted || isConfirmingDeletion) && /*#__PURE__*/React.createElement(Text, {
      as: "div",
      dimmed: true,
      fontSize: isDefaultSize ? 'xxs' : ['xxs', 's'],
      breakpoints: "m",
      pl: isDefaultSize ? 'xxs' : ['xxs', 'xs']
    }, /*#__PURE__*/React.createElement(InlineList, {
      splitBy: "dot",
      as: "div",
      itemAs: "div"
    }, !isNested && /*#__PURE__*/React.createElement(TextLink, {
      bold: true,
      as: "button",
      onClick: onReply
    }, t('actions_reply')), canEdit && /*#__PURE__*/React.createElement(TextLink, {
      bold: true,
      as: "button",
      onClick: function onClick() {
        return setIsEditing(true);
      }
    }, t('actions_edit')), canDelete && /*#__PURE__*/React.createElement(TextLink, {
      bold: true,
      as: "button",
      onClick: function onClick() {
        return setIsConfirmingDeletion(true);
      }
    }, t('actions_delete')), /*#__PURE__*/React.createElement(Time, {
      id: "comment-date-".concat(id),
      dateTime: createdTime
    }), isEdited && t('actions_edited'))), isConfirmingDeletion && /*#__PURE__*/React.createElement(DeleteConfirmation, {
      id: id,
      onConfirm: function onConfirm() {
        return onDeleteComment();
      },
      onCancel: function onCancel() {
        return setIsConfirmingDeletion(false);
      }
    })));
  }
}
export default Comment;